import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  TabKicker,
  TabSectionTitle,
  HeroCard,
  ChartCard,
  DataListCard,
  DataRow,
} from "./_shell";

/**
 * TabReviews — sequential editorial narrative.
 *   01 · COHORT SHAPE (bucket-mix bar)
 *   02 · GRADE SIGNATURE (data list)
 *   03 · SELF-REPORTED OFFERS (Step 5 preview)
 *
 * Below N < 3 → stylised dashed placeholder.
 * Testids preserved: tab-reviews.
 */

function BucketBar({ mix, n }) {
  const s = mix.safety || 0, t = mix.target || 0, r = mix.reach || 0;
  const total = s + t + r || 1;
  const seg = (v, colour, label) => {
    const pct = (v / total) * 100;
    if (pct < 0.5) return null;
    return (
      <div className="flex items-center justify-center text-white font-mono font-semibold"
           style={{ width: `${pct}%`, backgroundColor: colour, fontSize: 12 }}
           title={`${label} · ${v}`}>
        {pct > 8 ? v : ""}
      </div>
    );
  };
  return (
    <div>
      <div className="flex h-12 w-full border border-[#002147]/10">
        {seg(s, "#059669", "Safety")}
        {seg(t, "#7C3AED", "Target")}
        {seg(r, "#B45309", "Reach")}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 bg-emerald-600" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#002147]/60">Safety</span>
          <span className="font-mono text-sm text-[#002147] font-semibold ml-auto">{s}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 bg-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#002147]/60">Target</span>
          <span className="font-mono text-sm text-[#002147] font-semibold ml-auto">{t}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 bg-amber-700" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#002147]/60">Reach</span>
          <span className="font-mono text-sm text-[#002147] font-semibold ml-auto">{r}</span>
        </div>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
        Total applicants · {n}
      </p>
    </div>
  );
}

export default function TabReviews({ uni }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uni.id}/reviews-signals`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [uni.id]);

  const reviews = data?.reviews;
  const n = reviews?.n_submissions || 0;
  const hasOutcomes = reviews && (reviews.outcomes.accepted + reviews.outcomes.rejected + reviews.outcomes.waitlisted) > 0;

  if (loading) {
    return (
      <div className="p-6 font-mono text-[10px] uppercase tracking-widest text-[#002147]/40">
        Loading applicant signals…
      </div>
    );
  }

  if (!reviews || n < 3) {
    return (
      <section data-testid="tab-reviews">
        <div>
          <TabKicker>01 · Applicant Reports</TabKicker>
          <TabSectionTitle>Cohort snapshot pending</TabSectionTitle>
        </div>
        <div className="border-2 border-dashed border-[#002147]/20 p-12 text-center">
          <p className="font-serif text-lg text-[#002147]/70 leading-relaxed max-w-xl mx-auto">
            Fewer than 3 applicants have measured themselves against{" "}
            <span className="font-semibold">{uni.name}</span> yet.
          </p>
          <p className="mt-4 font-mono text-xs uppercase tracking-widest text-[#002147]/50">
            Once we reach 3, an anonymised cohort snapshot appears here
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-8" data-testid="tab-reviews">
      <div>
        <TabKicker>01 · Cohort Shape</TabKicker>
        <TabSectionTitle>Who's applying, and where the model places them</TabSectionTitle>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        <HeroCard
          className="md:col-span-4"
          kicker="Applicants"
          metric={n}
          subtitle="ran model against this uni"
          prose="Anonymised, model-derived grade-match results, not offer reports."
        />
        <ChartCard
          className="md:col-span-8"
          title="Bucket mix"
          legend={<span>Model classification: Safety · Target · Reach</span>}
        >
          <BucketBar mix={reviews.bucket_mix} n={n} />
        </ChartCard>
      </div>

      <div>
        <TabKicker>02 · Grade Signature</TabKicker>
        <TabSectionTitle>Who these applicants are</TabSectionTitle>
        <div className="grid md:grid-cols-2 gap-6">
          <DataListCard title="Curricula">
            {reviews.top_curricula.length ? reviews.top_curricula.map((c) => (
              <DataRow key={c.curriculum} label={c.curriculum} value={`${c.n}`} />
            )) : (
              <div className="p-4 font-sans text-sm text-[#002147]/60">—</div>
            )}
          </DataListCard>
          <DataListCard title="Modal grade signatures">
            {reviews.top_grade_signatures.length ? reviews.top_grade_signatures.slice(0, 5).map((g) => (
              <DataRow key={g.signature} label={g.signature.replace(/^a_level:/, "")} value={`${g.n}`} />
            )) : (
              <div className="p-4 font-sans text-sm text-[#002147]/60">—</div>
            )}
          </DataListCard>
        </div>
      </div>

      {hasOutcomes && (
        <div>
          <TabKicker>03 · Self-Reported Offers</TabKicker>
          <TabSectionTitle>What applicants heard back</TabSectionTitle>
          <div className="grid grid-cols-3 gap-px bg-[#002147]/10 border border-[#002147]/10">
            <div className="bg-[#F3F1EC] p-6 flex flex-col items-center">
              <span className="font-serif text-4xl text-emerald-800">{reviews.outcomes.accepted}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-800/80 mt-2">
                Accepted
              </span>
            </div>
            <div className="bg-[#F3F1EC] p-6 flex flex-col items-center">
              <span className="font-serif text-4xl text-amber-700">{reviews.outcomes.waitlisted}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-amber-700/80 mt-2">
                Waitlisted
              </span>
            </div>
            <div className="bg-[#F3F1EC] p-6 flex flex-col items-center">
              <span className="font-serif text-4xl text-rose-700">{reviews.outcomes.rejected}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-rose-700/80 mt-2">
                Rejected
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
